import { Uuid } from '../../models/Base';
import Repository, { Cursor, RepositoryJson } from '../../models/Repository';
import { AsyncActionOptions, FetchActionResult } from '../Store';

import {
  ConcreteGame,
  ConcreteGameJson,
  Game,
  GameJson,
  GameTypes,
} from './Game';
import { GameCollection, GameCollectionJson } from './GameCollection';
import { GameStatistics, GameStatisticsJson } from './GameStatistics';
import { Slideshow, SlideshowJson } from './Slideshow';

export type GameCollectionMapField = string;
export type GameCollectionListField = string;
export class GameCollectionRepository extends Repository<
  GameCollection,
  GameCollectionJson<Date | number>,
  GameCollectionMapField,
  GameCollectionListField
> {
  constructor(
    repository?: RepositoryJson<
      GameCollectionJson<Date | number>,
      GameCollectionMapField,
      GameCollectionListField,
      Date | number
    >,
  ) {
    super({ ...repository }, (x) => new GameCollection(x));
  }
}

export type GameMapField = string;
export type GameListField = string;
export class GameRepository extends Repository<
  Game,
  GameJson<Date | number>,
  GameMapField,
  GameListField
> {
  constructor(
    repository?: RepositoryJson<
      GameJson<Date | number>,
      GameMapField,
      GameListField,
      Date | number
    >,
  ) {
    super(
      { ...repository },
      <G extends keyof GameTypes>(x: ConcreteGameJson<G, Date | number>) =>
        new ConcreteGame<G>(x) as Game,
    );
  }
}

export type GameStatisticsMapField = string;
export type GameStatisticsListField = string;
export class GameStatisticsRepository extends Repository<
  GameStatistics,
  GameStatisticsJson,
  GameStatisticsMapField,
  GameStatisticsListField
> {
  constructor(
    repository?: RepositoryJson<
      GameStatisticsJson,
      GameStatisticsMapField,
      GameStatisticsListField,
      Date | number
    >,
  ) {
    super({ ...repository }, (x) => new GameStatistics(x));
  }
}

export type SlideshowMapField = string;
export type SlideshowListField = string;
export class SlideshowRepository extends Repository<
  Slideshow,
  SlideshowJson<Date | number>,
  SlideshowMapField,
  SlideshowListField
> {
  constructor(
    repository?: RepositoryJson<
      SlideshowJson<Date | number>,
      SlideshowMapField,
      SlideshowListField,
      Date | number
    >,
  ) {
    super({ ...repository }, (x) => new Slideshow(x));
  }
}

export interface GameStoreStateJson {
  gameCollections: RepositoryJson<
    GameCollectionJson,
    GameCollectionMapField,
    GameCollectionListField,
    number
  >;
  games: RepositoryJson<GameJson, GameMapField, GameListField, number>;
  // slideshows: RepositoryJson<
  //   SlideshowJson,
  //   SlideshowMapField,
  //   SlideshowListField,
  //   number
  // >;
  gameStatistics: RepositoryJson<
    GameStatisticsJson,
    GameStatisticsMapField,
    GameStatisticsListField,
    number
  >;
  // slideshowStatistics: Record<string, SlideshowStatisticsJson>; // slideshowId
}

export interface GameStoreState {
  gameCollections: GameCollectionRepository;
  games: GameRepository;
  // slideshows: SlideshowRepository;
  gameStatistics: GameStatisticsRepository;
  // slideshowStatistics: Record<string, SlideshowStatistics>;
  albumScrollPosition: number | null;
}

export interface GameStoreAction {
  // game collection
  getGameCollection: (
    gameCollectionId: Uuid,
  ) => GameCollection | null | undefined;
  getGameCollections: (fieldName: string) => GameCollection[];
  fetchGameCollectionsByIds: (
    params: {
      fieldName: string;
      gameCollectionIds: Uuid[];
    },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  fetchGameCollections: (
    params: {
      fieldName: string;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  // game
  // setGame: (game: Game) => void;
  // setGameNotFound: (gameId: Uuid) => void;
  getGame: (gameId: Uuid) => Game | null | undefined;
  fetchGame: (
    params: {
      fieldName: string;
      gameCollectionId: Uuid;
      gameId: Uuid;
      hash: string;
      scope?: string;
    },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  // slideshow
  // setSlideshow: (slideshows: Slideshow) => void;
  // setSlideshowNotFound: (slideshowId: Uuid) => void;
  // getSlideshow: (slideshowId: Uuid) => Slideshow | null | undefined;
  // fetchSlideshow: (
  //   gameCollectionId: Uuid,
  //   slideshowId: Uuid,
  //   hash: string,
  //   scope?: string,
  // ) => Promise<void>;
  // game statistics
  // setGameStatistics: (gameStatistics: GameStatistics) => void;
  getGameStatistics: (gameId: Uuid) => GameStatistics | null | undefined;
  getGameStatisticsList: (fieldName: Uuid) => GameStatistics[];
  // incrementGameStatistics: (
  //   gameId: Uuid,
  //   increment: GameStatistics['statistics'],
  // ) => void;
  fetchGameStatistics: (
    params: {
      fieldName: Uuid;
      gameCollectionId: Uuid;
      gameId: Uuid;
      hash: string;
      scope?: string;
    },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  fetchGameAndStatisticsByIds: (
    params: {
      fieldName: string;
      gameIds: {
        gameCollectionId: Uuid;
        gameId: Uuid;
        hash: string;
        scope?: string;
      }[];
    },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  // slideshow statistics
  // setSlideshowStatistics: (slideshowStatistics: SlideshowStatistics) => void;
  // getSlideshowStatistics: (
  //   slideshowId: Uuid,
  // ) => SlideshowStatistics | undefined;
  // incrementSlideshowStatistics: (
  //   slideshowId: Uuid,
  //   increment: SlideshowStatistics['statistics'],
  // ) => void;
  // fetchSlideshowStatistics: (
  //   gameCollectionId: Uuid,
  //   slideshowId: Uuid,
  //   scope?: string,
  // ) => Promise<void>;
  // prune listener
  onGameCollectionPrune: (gameCollection: GameCollection) => void;
  onGamePrune: (game: Game) => void;
  // onSlideshowPrune: (slideshow: Slideshow) => void;
  setAlbumScrollPosition: (position: number | null) => void;
  getAlbumScrollPosition: () => number | null;
}
